import { createContext, useCallback, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { TOUR_STEPS, firstStepIndexForPage, type TourStep } from './tourSteps';
import {
  trackQuickTourStarted,
  trackQuickTourStepViewed,
  trackQuickTourCompleted,
  trackQuickTourSkipped,
} from '@/lib/analytics';

/**
 * Tour statuses:
 *  idle       – no tour UI shown
 *  welcome    – welcome modal open
 *  active     – spotlight is showing steps
 *  waiting    – tour is paused, waiting for user to navigate (e.g. create project)
 *  completed  – completion modal open
 */
export type TourStatus = 'idle' | 'welcome' | 'active' | 'waiting' | 'completed';

interface TourContextType {
  status: TourStatus;
  currentStepIndex: number;
  currentStep: TourStep | null;
  currentPage: string | null;
  totalSteps: number;

  showWelcome: () => void;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  completeTour: (action: 'continue_setup' | 'done') => void;
  resetTour: () => void;
  goToStep: (stepId: string) => void;
  /** Pages call this on mount to let the tour know where we are */
  notifyPage: (page: 'dashboard' | 'new-shoot' | 'shoot-detail') => void;
  tourDismissed: boolean;
}

const TourContext = createContext<TourContextType>({
  status: 'idle',
  currentStepIndex: 0,
  currentStep: null,
  currentPage: null,
  totalSteps: TOUR_STEPS.length,
  showWelcome: () => {},
  startTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipTour: () => {},
  completeTour: () => {},
  resetTour: () => {},
  goToStep: () => {},
  notifyPage: () => {},
  tourDismissed: true,
});

const TOUR_STORAGE_KEY = 'prepframe_quick_tour';

interface TourState {
  started?: boolean;
  completed?: boolean;
  skipped?: boolean;
  stepIndex?: number;
  /** Which page the tour is waiting for */
  waitingForPage?: string;
}

function loadTourState(): TourState {
  try {
    const raw = localStorage.getItem(TOUR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveTourState(state: TourState) {
  localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(state));
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<TourStatus>('idle');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState<string | null>(null);
  const [tourDismissed, setTourDismissed] = useState(true);
  const initializedRef = useRef(false);
  const statusRef = useRef<TourStatus>(status);
  const userRef = useRef<User | null>(user);
  const currentPageRef = useRef<string | null>(null);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Async init logic extracted so it can be called from notifyPage too
  const runTourInit = useCallback(async (authUser: User) => {
    const localState = loadTourState();
    if (localState.completed || localState.skipped) {
      setTourDismissed(true);
      return;
    }

    const { data } = await supabase
      .from('profiles')
      .select('onboarding_completed_at')
      .eq('user_id', authUser.id)
      .maybeSingle();

    if (!data) {
      setTourDismissed(true);
      return;
    }

    const onboardingAt = (data as Record<string, unknown>).onboarding_completed_at as string | null;
    if (!onboardingAt) {
      // Onboarding not done yet – don't mark as initialized so we retry later
      initializedRef.current = false;
      return;
    }

    // User has completed onboarding and tour is not dismissed
    initializedRef.current = true;
    if (localState.started && localState.waitingForPage) {
      setCurrentStepIndex(localState.stepIndex ?? 0);
      setStatus('waiting');
      setTourDismissed(false);
    } else if (localState.started && localState.stepIndex != null) {
      setCurrentStepIndex(localState.stepIndex);
      setStatus('active');
      setTourDismissed(false);
    } else {
      setTourDismissed(false);
    }
  }, []);

  // Check if user should see the tour on mount
  useEffect(() => {
    if (!user) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;

    void runTourInit(user);
  }, [user, runTourInit]);

  const showWelcome = useCallback(() => {
    setStatus('welcome');
  }, []);

  const startTour = useCallback(() => {
    setCurrentStepIndex(0);
    setStatus('active');
    setTourDismissed(false);
    saveTourState({ started: true, stepIndex: 0 });
    trackQuickTourStarted();
  }, []);

  const nextStep = useCallback(() => {
    const nextIdx = currentStepIndex + 1;
    if (nextIdx >= TOUR_STEPS.length) {
      setStatus('completed');
      return;
    }

    const currentStep = TOUR_STEPS[currentStepIndex];
    const nextStepDef = TOUR_STEPS[nextIdx];
    const alreadyOnNextPage = nextStepDef ? currentPageRef.current === nextStepDef.page : false;

    // If next step is on a different page, pause the tour
    if (currentStep && nextStepDef && currentStep.page !== nextStepDef.page && !alreadyOnNextPage) {
      setCurrentStepIndex(nextIdx);
      setStatus('waiting');
      saveTourState({ started: true, stepIndex: nextIdx, waitingForPage: nextStepDef.page });
      return;
    }

    setCurrentStepIndex(nextIdx);
    saveTourState({ started: true, stepIndex: nextIdx });
    trackQuickTourStepViewed({ step_id: nextStepDef.id, step_index: nextIdx });
  }, [currentStepIndex]);

  const prevStep = useCallback(() => {
    const prevIdx = Math.max(0, currentStepIndex - 1);
    setCurrentStepIndex(prevIdx);
    saveTourState({ started: true, stepIndex: prevIdx });
  }, [currentStepIndex]);

  const skipTour = useCallback(() => {
    setStatus('idle');
    setTourDismissed(true);
    saveTourState({ skipped: true });
    trackQuickTourSkipped({ last_step_index: currentStepIndex });
  }, [currentStepIndex]);

  const completeTour = useCallback((action: 'continue_setup' | 'done') => {
    setStatus('idle');
    setTourDismissed(true);
    saveTourState({ completed: true });
    trackQuickTourCompleted({ action });
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setCurrentStepIndex(0);
    setStatus('idle');
    setTourDismissed(false);
  }, []);

  const goToStep = useCallback((stepId: string) => {
    const localState = loadTourState();
    if (localState.completed || localState.skipped || !localState.started) return;

    const targetIdx = TOUR_STEPS.findIndex((step) => step.id === stepId);
    if (targetIdx < 0) return;

    const targetStep = TOUR_STEPS[targetIdx];
    setCurrentStepIndex(targetIdx);
    setStatus('active');
    setTourDismissed(false);
    saveTourState({ started: true, stepIndex: targetIdx });
    trackQuickTourStepViewed({ step_id: targetStep.id, step_index: targetIdx });
  }, []);

  const notifyPage = useCallback((page: 'dashboard' | 'new-shoot' | 'shoot-detail') => {
    setCurrentPage(page);
    currentPageRef.current = page;

    // If tour init hasn't run yet (e.g. user was on onboarding when first checked),
    // trigger it now that we're on an actual tour-eligible page
    if (!initializedRef.current && userRef.current) {
      void runTourInit(userRef.current);
    }

    const localState = loadTourState();
    if (localState.completed || localState.skipped) return;
    if (!localState.started) return;

    const currentStatus = statusRef.current;

    // Case 1: Tour is paused specifically for this page
    if (localState.waitingForPage === page) {
      const targetIdx = localState.stepIndex ?? firstStepIndexForPage(page);
      setCurrentStepIndex(targetIdx);
      setStatus('active');
      saveTourState({ started: true, stepIndex: targetIdx });
      trackQuickTourStepViewed({ step_id: TOUR_STEPS[targetIdx]?.id ?? '', step_index: targetIdx });
      return;
    }

    // Case 2: Tour is active but current step is for a different page
    // (user clicked the highlighted element itself, navigating away)
    const currentStep = TOUR_STEPS[localState.stepIndex ?? 0];
    if (currentStatus === 'active' && currentStep && currentStep.page !== page) {
      const targetIdx = firstStepIndexForPage(page);
      if (targetIdx >= 0) {
        setCurrentStepIndex(targetIdx);
        setStatus('active');
        saveTourState({ started: true, stepIndex: targetIdx });
        trackQuickTourStepViewed({ step_id: TOUR_STEPS[targetIdx]?.id ?? '', step_index: targetIdx });
      }
    }
  }, [runTourInit]);

  const currentStep = status === 'active' ? TOUR_STEPS[currentStepIndex] ?? null : null;

  return (
    <TourContext.Provider value={{
      status,
      currentStepIndex,
      currentStep,
      currentPage,
      totalSteps: TOUR_STEPS.length,
      showWelcome,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      resetTour,
      goToStep,
      notifyPage,
      tourDismissed,
    }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  return useContext(TourContext);
}
